import React from "react";
import { AlertTriangle, CheckCircle2, ScanLine, ShieldAlert, Upload } from "lucide-react";
import { OCRProgress } from "../shared/OCRProgress";
import { MODAL_ACTIONS } from "../shared/Modal";
import { useAppContext } from "../../AppContext";

/* -----------------------------------------------------------------------------
 * PaymentProofForm — the amount + upload + submit block of the payment modal.
 * Extracted from PaymentProofModal.jsx so the parent modal only owns the shell
 * (cart summary + payment method selector). Everything payment-flow related —
 * credit toggle, amount input, manual reference fallback, image upload, OCR
 * progress, and the confirm/cancel buttons — lives here.
 * --------------------------------------------------------------------------- */
export function PaymentProofForm() {
  const ctx = useAppContext();
  const {
    submitting, cart, cartTotalBs,
    checkoutPmTab, paidAmountBs, setPaidAmountBs,
    referenceNumber, setReferenceNumber, needsManualRef, setNeedsManualRef,
    proofFile, setProofFile, setProofFileName, proofFileName,
    useAccountCredit, setUseAccountCredit,
    validationErrors, handleSubmitPayment,
    user, exchangeRate, paymentMethods, selectedPmId,
    formatBs, setShowPaymentModal,
  } = ctx;

  // El tipo REAL del método elegido manda sobre la pestaña visible: así el
  // rótulo ($ o Bs) y la conversión nunca se desincronizan.
  const selectedPm = (paymentMethods || []).find(pm => pm.id === selectedPmId);
  const isUsdPayment = (selectedPm?.type || checkoutPmTab) === "USD_PLATFORM";
  const availableCreditBs = Math.max(0, Number(user.credit_balance_bs) || 0);
  const creditAppliedBs = useAccountCredit ? Math.min(availableCreditBs, cartTotalBs) : 0;
  const remainingBs = Math.max(0, cartTotalBs - creditAppliedBs);
  const remainingUsd = remainingBs / (Number(exchangeRate) || 1);
  const fullyCoveredByCredit = useAccountCredit && creditAppliedBs >= cartTotalBs - 0.5;
  const partiallyCovered = useAccountCredit && creditAppliedBs > 0 && !fullyCoveredByCredit;

  const handleCreditToggleChange = (e) => {
    const checked = e.target.checked;
    setUseAccountCredit(checked);
    const remainderBs = Math.max(0, cartTotalBs - Math.min(availableCreditBs, cartTotalBs));
    if (!checked) {
      setPaidAmountBs("");
      return;
    }
    if (remainderBs <= 0.5) {
      setPaidAmountBs("");
    } else if (isUsdPayment) {
      setPaidAmountBs((remainderBs / (Number(exchangeRate) || 1)).toFixed(2));
    } else {
      setPaidAmountBs(String(Math.round(remainderBs)));
    }
  };

  const handleProofFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setProofFile(e.target.files[0]);
      setProofFileName(e.target.files[0].name);
      // Uploading a different image resets the manual-fallback state so the
      // OCR can retry on the new image.
      setNeedsManualRef(false);
      setReferenceNumber("");
    }
  };

  return (
    <>
      {validationErrors.length > 0 && (
        <div className="p-4 rounded-xl bg-red-950/40 border border-red-500/50 space-y-2" data-testid="validation-errors">
          <div className="flex items-center gap-2 text-red-300 font-bold text-xs">
            <ShieldAlert className="w-4 h-4" /> Comprobante Rechazado
          </div>
          <ul className="text-xs text-red-200 list-disc pl-5 space-y-1">
            {validationErrors?.map((err, i) => <li key={`err-${i}-${err}`}>{err}</li>)}
          </ul>
          <p className="text-[11px] text-yellow-200 pt-1">
            Verifica los datos del comprobante y vuelve a intentarlo con la imagen correcta.
          </p>
        </div>
      )}

      <form data-testid="payment-proof-form-form-1" onSubmit={handleSubmitPayment} className="space-y-4">
        {/* Saldo a favor toggle — placed FIRST so the user's choice drives the rest of the form */}
        {availableCreditBs > 0 && (
          <label
            className="flex items-center gap-3 rounded-xl border border-green-500/30 bg-green-950/20 px-3 py-2.5 text-xs text-zinc-200 cursor-pointer"
            data-testid="use-credit-toggle-wrapper"
          >
            <input
              type="checkbox"
              checked={useAccountCredit}
              onChange={handleCreditToggleChange}
              data-testid="use-credit-toggle"
              className="w-4 h-4 accent-green-500"
            />
            <span>
              Usar saldo a favor disponible: <strong className="text-green-400" data-testid="available-credit-amount">{formatBs(availableCreditBs)}</strong>
            </span>
          </label>
        )}

        {/* Payment fields — hidden entirely when credit covers 100% */}
        {!fullyCoveredByCredit && (
          <div>
            <label className="block text-xs font-mono text-zinc-400 mb-1">
              {isUsdPayment ? "Monto Pagado ($):" : "Monto Pagado (Bs):"}
            </label>
            <input
              type="number"
              step={isUsdPayment ? "0.01" : "1"}
              required
              data-testid="paid-amount-input"
              value={paidAmountBs}
              onChange={(e) => setPaidAmountBs(e.target.value)}
              placeholder={isUsdPayment ? remainingUsd.toFixed(2) : String(Math.round(remainingBs))}
              className="w-full bg-zinc-950 border border-zinc-700 rounded-xl px-4 py-2.5 text-white text-xs font-mono"
            />
            <p className="text-[10px] text-zinc-500 mt-1 font-mono">
              La referencia se lee automáticamente del comprobante · no necesitas escribirla.
            </p>
            {isUsdPayment && (
              <div
                data-testid="usd-amount-hint"
                className="mt-2 p-2.5 rounded-xl bg-green-950/25 border border-green-500/40 space-y-1"
              >
                <p className="text-[11px] text-green-200 font-mono">
                  Escribe el monto <strong>EN DÓLARES</strong>, igual al de tu comprobante de{" "}
                  Zinli o Binance Pay.
                </p>
                <p className="text-[10px] text-green-300/80 font-mono">
                  Equivale a{" "}
                  <strong>
                    Bs {Math.round((Number(paidAmountBs) || 0) * (Number(exchangeRate) || 0)).toLocaleString("es-VE")}
                  </strong>{" "}
                  a la tasa actual.
                </p>
                <p className="text-[10px] text-amber-300/90 font-mono">
                  ¿Pagaste en bolívares? Cambia arriba a «Pago Móvil» o «Transferencia».
                </p>
              </div>
            )}
          </div>
        )}

        {/* Manual reference fallback — only shown when the backend flagged
            that the AI could not read the reference from the uploaded proof. */}
        {!fullyCoveredByCredit && needsManualRef && !isUsdPayment && (
          <div
            data-testid="manual-reference-fallback"
            className="p-3 rounded-xl bg-amber-950/30 border border-amber-500/50 space-y-2"
          >
            <p className="text-xs font-mono uppercase tracking-widest text-amber-300 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" />
              No pudimos leer la referencia automáticamente
            </p>
            <p className="text-[11px] text-amber-100 leading-relaxed">
              Por favor ingrésala manualmente. El admin revisará el comprobante para su aprobación.
            </p>
            <input
              type="text"
              required
              pattern="[0-9]*"
              inputMode="numeric"
              data-testid="manual-reference-input"
              value={referenceNumber}
              onChange={(e) => setReferenceNumber(e.target.value.replace(/\D/g, ""))}
              placeholder="Escribe el número de referencia (solo dígitos)"
              className="w-full bg-zinc-950 border border-amber-500/60 rounded-xl px-4 py-2.5 text-white text-xs font-mono focus:outline-none focus:border-amber-400"
            />
          </div>
        )}

        {!fullyCoveredByCredit && (
          <div>
            <label className="block text-xs font-mono text-zinc-400 mb-1">Comprobante (Capture del pago):</label>
            <label htmlFor="proof-file-input" className="border-2 border-dashed border-zinc-700 rounded-xl p-4 text-center cursor-pointer hover:border-purple-500 bg-zinc-950 block">
              <Upload className="w-6 h-6 text-purple-400 mx-auto mb-1" />
              <p className="text-xs text-zinc-300">
                {proofFileName ? <strong className="text-green-400">{proofFileName}</strong> : "Sube tu captura de pago (Haz clic aquí)"}
              </p>
              <p className="text-[10px] text-zinc-500 mt-1">JPG, PNG o WEBP - máx 10MB</p>
              <input
                id="proof-file-input"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                data-testid="proof-file-input"
                onChange={handleProofFileChange}
                className="hidden"
              />
            </label>
          </div>
        )}

        {fullyCoveredByCredit && (
          <div
            data-testid="credit-full-cover-note"
            className="p-3 rounded-xl bg-green-950/30 border border-green-500/50 text-green-200 text-[12px] leading-relaxed"
          >
            <p className="font-bold flex items-center gap-2 mb-1">
              <CheckCircle2 className="w-4 h-4 text-green-400" />
              Pago cubierto 100% con tu saldo a favor
            </p>
            <p>
              Tus fondos cubren la totalidad de la compra ({formatBs(cartTotalBs)}).
              Al hacer clic en <strong className="text-white">Confirmar Pago</strong> se descontará de tu saldo a favor de forma inmediata.
            </p>
          </div>
        )}

        {/* Big, unambiguous breakdown when the credit doesn't cover
            the whole cart — shows exactly what's left to pay. */}
        {partiallyCovered && (
          <div
            data-testid="credit-breakdown"
            className="rounded-2xl bg-gradient-to-br from-emerald-950/40 to-cyan-950/30 border border-emerald-500/40 p-4"
          >
            <p className="text-[10px] font-mono uppercase tracking-widest text-emerald-300 mb-3">
              Desglose de pago con Saldo a Favor
            </p>
            <div className="space-y-1.5 text-xs">
              <div className="flex items-center justify-between text-zinc-300">
                <span>Total del pedido</span>
                <span className="font-mono">{formatBs(cartTotalBs)}</span>
              </div>
              <div className="flex items-center justify-between text-emerald-300">
                <span>Saldo a favor aplicado</span>
                <span className="font-mono">− {formatBs(creditAppliedBs)}</span>
              </div>
              <div className="h-px bg-emerald-500/30 my-2" />
              <div className="flex items-center justify-between text-white pt-1">
                <span className="text-[13px] font-black uppercase tracking-wide">
                  {isUsdPayment ? "A pagar en $" : "A pagar en Bs"}
                </span>
                <span data-testid="credit-remaining-amount" className="font-black text-lg text-cyan-300 font-mono">
                  {isUsdPayment ? `$${remainingUsd.toFixed(2)}` : formatBs(remainingBs)}
                </span>
              </div>
            </div>
            <p className="text-[11px] text-emerald-200/80 mt-3 leading-relaxed">
              Realiza {isUsdPayment ? "el pago en $" : "la transferencia o pago móvil"} por
              el monto exacto de arriba y adjunta el comprobante. Al confirmar,
              el sistema descontará automáticamente <strong className="text-white">{formatBs(creditAppliedBs)}</strong>
              {" "}de tu saldo.
            </p>
          </div>
        )}

        {!fullyCoveredByCredit && (
          <div className="p-3 rounded-xl bg-yellow-950/30 border border-yellow-500/30 text-yellow-200 text-[11px]">
            <p>
              <strong>⚠️ Verificación estricta:</strong> El monto pagado debe coincidir con {partiallyCovered ? "el restante a transferir" : "el total del carrito"}
              {" "}({partiallyCovered ? (isUsdPayment ? `$${remainingUsd.toFixed(2)}` : formatBs(remainingBs)) : formatBs(cartTotalBs)}).
              Nuestro sistema analizará el comprobante con IA (teléfono, banco, referencia y fecha de hoy).
            </p>
            <p className="mt-1">Tiempo estimado tras aprobación: <strong>5-15 min</strong>.</p>
          </div>
        )}

        {/* Animated OCR feedback while the backend verifies the receipt. Only
            shown for bank flows (i.e. there's a proof to analyse). Auto-unmounts
            when submitting flips back to false. */}
        {submitting && proofFile && <OCRProgress />}

        <div className={MODAL_ACTIONS}>
          <button data-testid="payment-proof-form-button-2"
            type="button"
            onClick={() => setShowPaymentModal(false)}
            disabled={submitting}
            className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white font-bold py-3 rounded-xl text-xs disabled:opacity-40"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={submitting}
            data-testid="confirm-payment-submit-btn"
            className="flex-1 bg-gradient-to-r from-purple-600 to-cyan-600 hover:opacity-90 text-white font-bold py-3 rounded-xl text-xs shadow-lg disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {submitting ? <><ScanLine className="w-4 h-4 animate-pulse" /> Analizando comprobante…</> : "Confirmar Pago"}
          </button>
        </div>
      </form>
    </>
  );
}
